import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Phone,
  Warning,
  ClockCountdown,
  ArrowClockwise,
  CalendarPlus,
  Note,
  MagnifyingGlass,
  MapPin,
  ArrowSquareOut,
  CalendarBlank,
} from "@phosphor-icons/react";
import { TopNav, bumpCallbackAgenda } from "@/components/gbp/TopNav";
import { DailySummary } from "@/components/gbp/DailySummary";
import {
  LeadNotesDialog,
  LeadStatusBadge,
} from "@/components/gbp/LeadNotesDialog";
import { fetchCallbackAgenda, upsertLead } from "@/lib/api";

function formatWhen(iso, tz) {
  if (!iso) return "—";
  const d = new Date(iso);
  const opts = {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  };
  if (tz) opts.timeZone = tz;
  return d.toLocaleString(undefined, opts);
}

function tzShortLabel(tz) {
  if (!tz) return "";
  // Try to get a short name like "AEST" via toLocaleTimeString parts;
  // fall back to a compact label derived from the IANA name.
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(new Date());
    const zn = parts.find((p) => p.type === "timeZoneName")?.value;
    if (zn) return zn;
  } catch (e) {
    // fall through
  }
  const short = tz.split("/").pop() || tz;
  return short.replace(/_/g, " ");
}

function isOutsideBusinessHours(iso, tz) {
  if (!iso || !tz) return false;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "numeric",
      hour12: false,
    }).formatToParts(new Date(iso));
    const h = parseInt(parts.find((p) => p.type === "hour")?.value, 10);
    if (Number.isNaN(h)) return false;
    return h < 8 || h >= 19;
  } catch (e) {
    return false;
  }
}

function overdueBy(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days >= 1) return `${days}d overdue`;
  const hrs = Math.floor(diff / 3600000);
  if (hrs >= 1) return `${hrs}h overdue`;
  return "just overdue";
}

function nextMondayIso(hour = 9) {
  const d = new Date();
  const day = d.getDay();
  // 0=Sun, 1=Mon, ... 6=Sat
  const daysUntilMon = day === 1 ? 7 : (8 - day) % 7 || 7;
  d.setDate(d.getDate() + daysUntilMon);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString();
}

function plusDaysIso(days, hour = null) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  if (hour != null) d.setHours(hour, 0, 0, 0);
  return d.toISOString();
}

export default function Callbacks() {
  const [agenda, setAgenda] = useState({
    overdue: [],
    today: [],
    upcoming: [],
    counts: { overdue: 0, today: 0, upcoming: 0, actionable: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [notesTarget, setNotesTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCallbackAgenda();
      setAgenda(data);
    } catch (e) {
      toast.error("Could not load callbacks");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const snooze = async (lead, newIso, label) => {
    const placeId = lead.place_id;
    if (!placeId) return;
    try {
      await upsertLead(placeId, { callback_at: newIso });
      toast.success(`Snoozed to ${label}`);
      bumpCallbackAgenda();
      load();
    } catch (e) {
      toast.error("Could not snooze");
    }
  };

  const openNotes = (lead) => {
    // Build a business-shaped object from the lead so LeadNotesDialog can save phone/etc back too.
    const business = {
      place_id: lead.place_id,
      title: lead.business_name || "Business",
      phone: lead.phone,
      gbp_url: lead.gbp_url,
      address: lead.address,
    };
    setNotesTarget({
      business,
      lead,
      scanContext: { niche: lead.niche, area: lead.area },
    });
  };

  const isEmpty =
    !loading &&
    agenda.overdue.length === 0 &&
    agenda.today.length === 0 &&
    agenda.upcoming.length === 0;

  return (
    <div data-testid="callbacks-page" className="min-h-screen bg-white">
      <TopNav />
      <main className="mx-auto max-w-6xl px-6 pb-24 pt-8 md:px-10">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <div className="mb-1 inline-flex items-center gap-1.5 rounded-full border border-electric/30 bg-electric-soft px-2.5 py-1 text-xs font-medium text-electric">
              Daily agenda
            </div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Today&apos;s callbacks
            </h1>
            <p className="mt-1 font-sans text-sm text-muted-foreground">
              Every lead you owe a callback to, across every scan. Sorted by
              urgency.
            </p>
          </div>
          <button
            type="button"
            data-testid="refresh-callbacks"
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-2 font-sans text-sm text-ink transition-colors hover:border-electric hover:text-electric"
          >
            <ArrowClockwise size={14} weight="bold" />
            Refresh
          </button>
        </div>

        <StatsRow counts={agenda.counts} />

        <div className="mt-6" />
        <DailySummary refreshKey={agenda?.counts?.actionable} />

        {loading ? (
          <div className="mt-6 space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 rounded-lg gbp-shimmer" />
            ))}
          </div>
        ) : isEmpty ? (
          <EmptyState />
        ) : (
          <div className="mt-6 space-y-8">
            {agenda.overdue.length > 0 && (
              <Section
                testid="section-overdue"
                title="Overdue"
                subtitle={`${agenda.overdue.length} · call these first`}
                tone="overdue"
              >
                {agenda.overdue.map((lead) => (
                  <LeadCard
                    key={lead.place_id}
                    lead={lead}
                    urgency="overdue"
                    onNotes={() => openNotes(lead)}
                    onSnooze={snooze}
                  />
                ))}
              </Section>
            )}

            {agenda.today.length > 0 && (
              <Section
                testid="section-today"
                title="Today"
                subtitle={`${agenda.today.length} · scheduled`}
                tone="today"
              >
                {agenda.today.map((lead) => (
                  <LeadCard
                    key={lead.place_id}
                    lead={lead}
                    urgency="today"
                    onNotes={() => openNotes(lead)}
                    onSnooze={snooze}
                  />
                ))}
              </Section>
            )}

            {agenda.upcoming.length > 0 && (
              <Section
                testid="section-upcoming"
                title="Upcoming"
                subtitle={`next ${agenda.upcoming.length}`}
                tone="upcoming"
              >
                {agenda.upcoming.map((lead) => (
                  <LeadCard
                    key={lead.place_id}
                    lead={lead}
                    urgency="upcoming"
                    onNotes={() => openNotes(lead)}
                    onSnooze={snooze}
                  />
                ))}
              </Section>
            )}
          </div>
        )}
      </main>

      <LeadNotesDialog
        open={!!notesTarget}
        onOpenChange={(v) => !v && setNotesTarget(null)}
        business={notesTarget?.business}
        currentLead={notesTarget?.lead}
        scanContext={notesTarget?.scanContext}
        onSaved={() => {
          bumpCallbackAgenda();
          load();
        }}
      />
    </div>
  );
}

function StatsRow({ counts }) {
  const items = [
    { label: "Overdue", value: counts.overdue, tone: "text-[#C92A2A]" },
    { label: "Today", value: counts.today, tone: "text-[#B45309]" },
    { label: "Upcoming", value: counts.upcoming, tone: "text-ink" },
    { label: "Actionable now", value: counts.actionable, tone: "text-electric" },
  ];
  return (
    <div
      data-testid="callbacks-stats"
      className="grid grid-cols-2 gap-3 sm:grid-cols-4"
    >
      {items.map((it) => (
        <div
          key={it.label}
          className="rounded-lg border border-border bg-white px-4 py-3"
        >
          <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {it.label}
          </div>
          <div className={`mt-1 font-display text-2xl font-bold tabular-nums ${it.tone}`}>
            {it.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function Section({ title, subtitle, tone, children, testid }) {
  const toneClasses = {
    overdue: "border-l-4 border-l-[#C92A2A]",
    today: "border-l-4 border-l-[#B45309]",
    upcoming: "border-l-4 border-l-electric",
  }[tone];
  return (
    <section data-testid={testid} className={`overflow-hidden rounded-lg bg-white ${toneClasses}`}>
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="font-display text-lg font-bold text-ink">{title}</div>
        <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          {subtitle}
        </div>
      </div>
      <div className="divide-y divide-border/60">{children}</div>
    </section>
  );
}

function LeadCard({ lead, urgency, onNotes, onSnooze }) {
  return (
    <div
      data-testid={`callback-card-${lead.place_id}`}
      className="grid grid-cols-1 gap-3 px-4 py-3 md:grid-cols-[1.4fr_auto_auto] md:items-center"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className="truncate font-sans font-semibold text-ink">
            {lead.business_name || "(unnamed business)"}
          </div>
          <LeadStatusBadge status={lead.status || "not_contacted"} />
          {urgency === "overdue" ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-[#FECACA] bg-[#FCE8E8] px-2 py-0.5 text-[10px] font-medium text-[#C92A2A]">
              <Warning size={10} weight="fill" />
              {overdueBy(lead.callback_at)}
            </span>
          ) : urgency === "today" ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-[#FDE68A] bg-[#FEF3C7] px-2 py-0.5 text-[10px] font-medium text-[#92400E]">
              <ClockCountdown size={10} weight="fill" />
              Today
            </span>
          ) : null}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-sans text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <CalendarBlank size={11} weight="bold" />
            {formatWhen(lead.callback_at)}
            <span className="ml-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground/80">
              your time
            </span>
          </span>
          {lead.target_tz &&
          lead.target_tz !==
            (typeof Intl !== "undefined" &&
              Intl.DateTimeFormat().resolvedOptions().timeZone) ? (
            <span
              data-testid={`target-tz-${lead.place_id}`}
              className="inline-flex items-center gap-1 rounded-md bg-electric-soft px-1.5 py-0.5 font-mono text-[10px] text-electric"
              title={`Target is in ${lead.target_tz}`}
            >
              ↳ {formatWhen(lead.callback_at, lead.target_tz)} {tzShortLabel(lead.target_tz)}
              {isOutsideBusinessHours(lead.callback_at, lead.target_tz) ? (
                <span className="ml-1 rounded-sm bg-[#C92A2A] px-1 py-0.5 text-[9px] font-bold text-white">
                  outside hrs
                </span>
              ) : null}
            </span>
          ) : null}
          {lead.niche || lead.area ? (
            <span className="inline-flex items-center gap-1">
              <MagnifyingGlass size={11} weight="bold" />
              {lead.niche || "—"}
              {lead.area ? (
                <>
                  <MapPin size={11} weight="bold" className="ml-1" />
                  {lead.area}
                </>
              ) : null}
            </span>
          ) : null}
        </div>
        {lead.remark ? (
          <div
            className="mt-1 line-clamp-2 font-sans text-sm text-ink"
            title={lead.remark}
          >
            {lead.remark}
          </div>
        ) : (
          <div className="mt-1 font-sans text-xs italic text-muted-foreground">
            no remark yet
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {lead.phone ? (
          <a
            href={`tel:${lead.phone.replace(/\s+/g, "")}`}
            data-testid={`call-${lead.place_id}`}
            className="inline-flex items-center gap-1.5 rounded-md bg-electric px-3 py-2 font-sans text-xs font-semibold text-white transition-transform hover:-translate-y-[1px] hover:bg-electric-hover"
          >
            <Phone size={13} weight="bold" />
            {lead.phone}
          </a>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            no phone
          </span>
        )}
      </div>

      <div className="flex items-center gap-1.5">
        <SnoozeMenu lead={lead} onSnooze={onSnooze} />
        <button
          type="button"
          data-testid={`notes-${lead.place_id}`}
          onClick={onNotes}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-white px-2.5 py-2 font-sans text-xs text-ink transition-colors hover:border-electric hover:text-electric"
        >
          <Note size={12} weight="bold" />
          Notes
        </button>
        {lead.gbp_url ? (
          <a
            href={lead.gbp_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-md border border-border bg-white p-2 text-muted-foreground transition-colors hover:border-electric hover:text-electric"
            aria-label="Open GBP"
          >
            <ArrowSquareOut size={12} weight="bold" />
          </a>
        ) : null}
      </div>
    </div>
  );
}

function SnoozeMenu({ lead, onSnooze }) {
  const [open, setOpen] = useState(false);
  const options = [
    { label: "+1 day", iso: () => plusDaysIso(1, 9), labelFull: "tomorrow 9am" },
    { label: "+3 days", iso: () => plusDaysIso(3, 9), labelFull: "3 days · 9am" },
    { label: "Next Mon", iso: () => nextMondayIso(9), labelFull: "next Monday · 9am" },
  ];
  return (
    <div className="relative">
      <button
        type="button"
        data-testid={`snooze-${lead.place_id}`}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-md border border-border bg-white px-2.5 py-2 font-sans text-xs text-ink transition-colors hover:border-electric hover:text-electric"
      >
        <CalendarPlus size={12} weight="bold" />
        Snooze
      </button>
      {open ? (
        <div
          className="absolute right-0 top-full z-10 mt-1 min-w-[160px] overflow-hidden rounded-md border border-border bg-white shadow-lg"
          onMouseLeave={() => setOpen(false)}
        >
          {options.map((o) => (
            <button
              key={o.label}
              type="button"
              data-testid={`snooze-opt-${lead.place_id}-${o.label.replace(/\s+/g, "-").replace("+", "plus")}`}
              onClick={() => {
                setOpen(false);
                onSnooze(lead, o.iso(), o.labelFull);
              }}
              className="block w-full px-3 py-2 text-left font-sans text-sm text-ink transition-colors hover:bg-electric-soft hover:text-electric"
            >
              {o.label}
              <span className="ml-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {o.labelFull}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="empty-callbacks"
      className="mt-8 rounded-lg border border-dashed border-border bg-surface px-6 py-16 text-center"
    >
      <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-electric-soft text-electric">
        <Phone size={18} weight="fill" />
      </div>
      <div className="font-display text-lg font-semibold text-ink">
        No callbacks scheduled
      </div>
      <div className="mt-1 font-sans text-sm text-muted-foreground">
        Set a callback date on any lead&apos;s Notes dialog and it&apos;ll
        appear here. Perfect time to run a fresh scan.
      </div>
    </div>
  );
}
